/* ==========================================================================
   Royal Care Professional — header stack behaviour
   Behaviour for rc-topbar, rc-marquee and rc-navbar. Loaded once from
   layout/theme.liquid (the header group is on every page); the sections used to
   carry their own tags, which ran this file two or three times per page.
   Everything below is still idempotent: init() only touches nodes that are not
   yet flagged, and the theme editor calls it again on every section load.
   ========================================================================== */

(function () {
  'use strict';

  if (window.RCHeader) {
    window.RCHeader.init();
    return;
  }

  var FLAG = 'rcInit';
  var MOBILE_QUERY = '(max-width: 1023px)';

  function each(root, selector, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(selector), fn);
  }

  /** Marks a node as initialised. Returns false if it already was. */
  function claim(el) {
    if (el.dataset[FLAG] === 'true') return false;
    el.dataset[FLAG] = 'true';
    return true;
  }

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode — the bar simply reappears next visit */
    }
  }

  /* ------------------------------------------------------------------------
     Top bar
     ------------------------------------------------------------------------ */

  function initTopbar(el) {
    if (!claim(el)) return;

    var storageKey = el.dataset.storageKey;

    if (el.dataset.dismissible === 'true' && readStorage(storageKey) === 'dismissed') {
      el.hidden = true;
      return;
    }

    var closeBtn = el.querySelector('[data-rc-topbar-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        el.hidden = true;
        writeStorage(storageKey, 'dismissed');
      });
    }

    if (el.dataset.rotate !== 'true') return;

    var messages = el.querySelectorAll('[data-rc-topbar-msg]');
    if (messages.length < 2) return;

    var speed = parseInt(el.dataset.rotateSpeed, 10) || 5000;
    var index = 0;
    var timer = null;

    function show(next) {
      messages[index].classList.remove('is-active');
      messages[index].setAttribute('aria-hidden', 'true');
      index = next;
      messages[index].classList.add('is-active');
      messages[index].removeAttribute('aria-hidden');
    }

    function start() {
      stop();
      timer = window.setInterval(function () {
        show((index + 1) % messages.length);
      }, speed);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    // Don't burn cycles while the tab is hidden, and let people read on hover.
    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', start);
    el.addEventListener('focusin', stop);
    el.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    start();
  }

  /* ------------------------------------------------------------------------
     Marquee
     ------------------------------------------------------------------------ */

  function initMarquee(el) {
    if (!claim(el)) return;

    var track = el.querySelector('[data-rc-marquee-track]');
    if (!track) return;

    var groups = track.querySelectorAll('[data-rc-marquee-group]');
    if (!groups.length) return;

    var template = groups[0];

    /*
     * The CSS animation shifts the track by one group's width. For the loop to
     * look continuous the track has to be at least twice the viewport, so we
     * clone whole groups until it is, then tell CSS how many there are.
     */
    function fill() {
      // Drop earlier clones so a resize recalculates from a clean slate.
      each(track, '[data-rc-marquee-clone]', function (clone) {
        clone.remove();
      });

      var groupWidth = template.getBoundingClientRect().width;
      var viewport = el.getBoundingClientRect().width;
      if (!groupWidth || !viewport) return;

      var needed = Math.max(2, Math.ceil((viewport * 2) / groupWidth));
      var current = track.querySelectorAll('[data-rc-marquee-group]').length;

      for (var i = current; i < needed; i++) {
        var clone = template.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-rc-marquee-clone', '');
        each(clone, 'a', function (link) {
          link.setAttribute('tabindex', '-1');
        });
        track.appendChild(clone);
      }

      var total = track.querySelectorAll('[data-rc-marquee-group]').length;
      el.style.setProperty('--rc-mq-groups', String(total));
    }

    /*
     * Measuring forces a layout of the whole page, and at start-up the page is
     * still settling, so this waits for an idle moment. Nothing is lost in the
     * meantime: the section renders two groups, which is already a seamless
     * loop at any ordinary width.
     */
    var whenIdle = window.requestIdleCallback
      ? function (fn) {
          window.requestIdleCallback(fn, { timeout: 3000 });
        }
      : function (fn) {
          window.setTimeout(fn, 300);
        };

    whenIdle(fill);

    // Fonts and emoji land after first paint and change the measured width.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(function () {
          whenIdle(fill);
        })
        .catch(function () {});
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(fill, 200);
    });
  }

  /* ------------------------------------------------------------------------
     Navbar
     ------------------------------------------------------------------------ */

  function initNav(el) {
    if (!claim(el)) return;

    initFit(el);
    initSticky(el);
    initDropdowns(el);
    initDrawer(el);
    initAccordions(el);
    initSearch(el);
  }

  /**
   * The search icon opens Horizon's predictive search dialog (#search-modal,
   * rendered once in theme.liquid) instead of leaving for the search page.
   *
   * It stays a real link underneath: until dialog.js has defined the element,
   * on a page without the modal, or on a modified click, it navigates exactly
   * as it always did.
   */
  function initSearch(el) {
    each(el, '[data-rc-search-open]', function (link) {
      link.addEventListener('click', function (event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var modal = document.getElementById('search-modal');
        if (!modal || typeof modal.showDialog !== 'function') return;

        event.preventDefault();
        modal.showDialog();
      });
    });
  }

  /**
   * Keeps the desktop menu on one line.
   *
   * A wrapped navbar is the failure mode this replaces: the second row pushes
   * the icons down, the bar doubles in height and the whole page shifts. So
   * wrapping is switched off in CSS and the width problem is solved here
   * instead — by making the type smaller, which is what a person would do.
   *
   * --rc-nav-fit scales both the link size and the gap between links. The
   * steps are walked in order and the first one that fits wins, so a menu that
   * only just overflows loses a hair of tracking rather than a whole point of
   * type. Past the last step the links would be too small to read, and the
   * menu goes to the drawer instead.
   *
   * Measured, never guessed at a breakpoint: the merchant can rename every
   * link in the admin and the bar re-fits itself on the next load.
   *
   * The measuring itself is window.RCNavFit, inline in rc-navbar.liquid, so
   * the bar is fitted the moment it is parsed. This file is deferred, and
   * waiting for it painted the unfitted bar for as long as the rest of the
   * page took to arrive. What stays here is re-running it whenever one side
   * of the sum changes.
   */
  function initFit(el) {
    if (typeof window.RCNavFit !== 'function') return;

    function fit() {
      window.RCNavFit(el);
    }

    fit();

    // The logo is the other half of the sum, and a web font changes every
    // link's width — both land after the first measurement.
    var logo = el.querySelector('.rc-nav__logo');
    if (logo && !logo.complete) logo.addEventListener('load', fit);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fit).catch(function () {});
    }

    // Deliberately the resize event and not a ResizeObserver: this function
    // changes the layout it would be observing, and the compact class it can
    // add hides the very element being watched.
    var timer;
    window.addEventListener('resize', function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(fit, 150);
    });
  }

  function initSticky(el) {
    if (el.dataset.sticky !== 'true') return;

    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    el.parentNode.insertBefore(sentinel, el);

    var spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.display = 'none';
    el.parentNode.insertBefore(spacer, el.nextSibling);

    var observer = new IntersectionObserver(
      function (entries) {
        var stuck = !entries[0].isIntersecting;
        el.classList.toggle('is-stuck', stuck);
        // A fixed header leaves the flow, so hold its height open behind it.
        spacer.style.height = stuck ? el.offsetHeight + 'px' : '';
        spacer.style.display = stuck ? 'block' : 'none';
      },
      { rootMargin: '0px', threshold: 0 }
    );

    observer.observe(sentinel);
  }

  function initDropdowns(el) {
    each(el, '[data-rc-dropdown]', function (item) {
      var trigger = item.querySelector('[data-rc-dropdown-trigger]');
      var panel = item.querySelector('[data-rc-dropdown-panel]');
      if (!trigger || !panel) return;

      function close() {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }

      function open() {
        // Only one panel at a time.
        each(el, '[data-rc-dropdown].is-open', function (other) {
          if (other === item) return;
          other.classList.remove('is-open');
          var otherTrigger = other.querySelector('[data-rc-dropdown-trigger]');
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        });
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', close);

      // Keyboard: the arrow key opens the panel, the link itself still navigates.
      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          open();
          var first = panel.querySelector('a');
          if (first) first.focus();
        } else if (event.key === 'Escape') {
          close();
        }
      });

      panel.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        close();
        trigger.focus();
      });

      item.addEventListener('focusout', function (event) {
        if (item.contains(event.relatedTarget)) return;
        close();
      });
    });
  }

  function initDrawer(el) {
    var toggle = el.querySelector('[data-rc-nav-toggle]');
    var drawer = el.querySelector('[data-rc-nav-drawer]');
    var overlay = el.querySelector('[data-rc-nav-overlay]');
    var closeBtn = el.querySelector('[data-rc-nav-close]');
    if (!toggle || !drawer || !overlay) return;

    var lastFocus = null;
    var hideTimer = null;

    function open() {
      // A reopen inside the closing slide would otherwise be hidden again
      // when the previous close's timer fires.
      window.clearTimeout(hideTimer);
      lastFocus = document.activeElement;
      // Lifts the whole bar over the rest of the header group — see the drawer
      // note in rc-header.css. Held until the drawer has slid fully back out.
      el.classList.add('is-drawer-open');
      drawer.hidden = false;
      overlay.hidden = false;
      // Next frame, so the transition has a start state to animate from.
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        overlay.classList.add('is-visible');
      });
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('a, button, input');
      if (first) first.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      hideTimer = window.setTimeout(function () {
        drawer.hidden = true;
        overlay.hidden = true;
        el.classList.remove('is-drawer-open');
      }, 340);
      if (lastFocus) lastFocus.focus();
    }

    function isOpen() {
      return drawer.classList.contains('is-open');
    }

    toggle.addEventListener('click', function () {
      isOpen() ? close() : open();
    });
    overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) close();
    });

    // Trap focus inside the drawer while it owns the screen.
    drawer.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var focusables = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Rotating to desktop while the drawer is open would strand body overflow.
    var mq = window.matchMedia(MOBILE_QUERY);
    var onChange = function (event) {
      if (!event.matches && isOpen()) close();
    };
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
  }

  function initAccordions(el) {
    each(el, '[data-rc-accordion]', function (button) {
      var panel = button.nextElementSibling;
      if (!panel) return;

      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      });
    });
  }

  /* ------------------------------------------------------------------------
     Live counts
     ------------------------------------------------------------------------ */

  function paintBadge(node, count) {
    if (!node) return;
    var previous = parseInt(node.textContent, 10) || 0;
    node.textContent = String(count);
    node.classList.toggle('is-empty', count === 0);
    if (count !== previous) {
      node.classList.remove('is-bumped');
      // Force a reflow so the animation restarts on repeat additions.
      void node.offsetWidth;
      node.classList.add('is-bumped');
    }
  }

  function refreshCart() {
    var badges = document.querySelectorAll('[data-rc-cart-count]');
    if (!badges.length) return;

    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        Array.prototype.forEach.call(badges, function (badge) {
          paintBadge(badge, cart.item_count);
        });
      })
      .catch(function () {});
  }

  function refreshWishlist() {
    var badges = document.querySelectorAll('[data-rc-wishlist-count]');
    if (!badges.length) return;

    var count = 0;
    var raw = readStorage('rc-wishlist');
    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) count = parsed.length;
      } catch (e) {
        count = 0;
      }
    }

    Array.prototype.forEach.call(badges, function (badge) {
      paintBadge(badge, count);
    });
  }

  /**
   * The theme and its apps mutate the cart through several different code
   * paths, so rather than guessing at one event name we watch the network:
   * any successful write to a /cart/ endpoint triggers a re-read.
   */
  function watchCartRequests() {
    if (!window.fetch) return;

    var nativeFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      return nativeFetch.apply(this, args).then(function (response) {
        try {
          var input = args[0];
          var url = typeof input === 'string' ? input : input && input.url;
          if (url && /\/cart\/(add|change|update|clear)/.test(url) && response.ok) {
            window.setTimeout(refreshCart, 60);
          }
        } catch (e) {
          /* never let instrumentation break a real request */
        }
        return response;
      });
    };

    // Themes and apps that announce their own updates get honoured too.
    ['cart:updated', 'cart:refresh', 'CartUpdateEvent', 'cart:change'].forEach(function (name) {
      document.addEventListener(name, function () {
        window.setTimeout(refreshCart, 60);
      });
    });

    // Another tab changed the wishlist.
    window.addEventListener('storage', function (event) {
      if (event.key === 'rc-wishlist') refreshWishlist();
    });

    document.addEventListener('rc:wishlist:updated', refreshWishlist);

    // Back/forward cache restores a stale badge.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        refreshCart();
        refreshWishlist();
      }
    });
  }

  /* ------------------------------------------------------------------------
     Bootstrap
     ------------------------------------------------------------------------ */

  var watching = false;

  var RCHeader = {
    init: function (root) {
      var scope = root || document;
      each(scope, '[data-rc-topbar]', initTopbar);
      each(scope, '[data-rc-marquee]', initMarquee);
      each(scope, '[data-rc-nav]', initNav);

      refreshWishlist();

      if (!watching) {
        watching = true;
        watchCartRequests();
      }
    },
    refreshCart: refreshCart,
    refreshWishlist: refreshWishlist
  };

  window.RCHeader = RCHeader;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      RCHeader.init();
    });
  } else {
    RCHeader.init();
  }

  // Theme editor: sections are re-rendered in place, so re-scan the new markup.
  document.addEventListener('shopify:section:load', function (event) {
    RCHeader.init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    document.body.style.overflow = '';
  });
})();
